import useWorkspaces from "../../query/workspace/useWorkspaces";
import React, {useCallback, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useIntl} from "react-intl";
import {isSearchType, SearchType, SearchTypeEnum} from "@/respsitory/WorkspaceRepository";
import {SiteBunnyAdminError} from "@/error";

import CategorySearchBar from "../../component/CategorySearchBar";
import PageBar from "../../component/PageBar";
import WorkspaceRow from "./WorkspaceRow";
import {Workspace, workspacePage} from "@/model/workspace";

const PaginationSize = 10;
const WorkspaceView: React.FC = () => {
    const intl = useIntl();

    const [searchParams, setSearchParams] = useSearchParams();
    const [showDeletedWorkspace, setShowDeletedWorkspace] = useState<boolean>(false);
    const [showImportantWorkspace, setShowImportantWorkspace] = useState<boolean>(false);

    const searchCategory = useMemo(() => {
        return [
            {
                category: SearchTypeEnum.WORKSPACENAME,
                expression: intl.formatMessage({id: 'i1001'})
            },
            {
                category: SearchTypeEnum.USEREMAIL,
                expression: intl.formatMessage({id: 'i10031'})
            },
        ]
    }, [intl])

    const defaultPage: number = useMemo(() => {
        return parseInt(searchParams.get("page") ?? "1")
    }, [searchParams]);

    const defaultCategory: SearchType | undefined = useMemo(() => {
        return isSearchType(searchParams.get("category")) ? searchParams.get("category") as SearchType : undefined
    }, [searchParams]);

    const defaultKeyword = useMemo(() => {
        return searchParams.get("keyword") ?? undefined
    }, [searchParams]);

    const onSubmitKeyword = useCallback((category: string, keyword: string) => {
        setSearchParams({category, keyword})
    }, [setSearchParams])

    const {data: pagination} = useWorkspaces(
        {
            page: defaultPage,
            size: PaginationSize,
            searchType: defaultCategory,
            searchKeyword: defaultKeyword,
            deleted: showDeletedWorkspace,
            important: showImportantWorkspace
        }
    );
    const lastPage = useMemo(() => {
        if (!pagination) return 0;

        return Math.floor(pagination.count / PaginationSize) + ((pagination.count % PaginationSize) && 1);
    }, [pagination])

    if ((lastPage > 0 && defaultPage > lastPage) || defaultPage < 1) {
        throw new SiteBunnyAdminError("404", "Improper page number request");
    }

    const onClickPage = useCallback((page: number) => {
        searchParams.set("page", page.toString());
        setSearchParams(searchParams)

    }, [searchParams, setSearchParams]);

    return (
        <div className={"flex flex-col h-full text-[16px]"}>
            <div className="h-full px-8">
                <div className="flex justify-between">
                    <label className="text-2xl font-bold"></label>
                    <div>
                        <input type="checkbox"
                               onChange={() => {
                                   onClickPage(1);
                                   setShowImportantWorkspace(!showImportantWorkspace);
                               }}
                               className="mx-2"/>
                        <label className="text-lg">
                            {intl.formatMessage({id: "i11113"})}
                        </label>
                        <input type="checkbox"
                               onChange={() => {
                                   onClickPage(1);
                                   setShowDeletedWorkspace(!showDeletedWorkspace);
                               }}
                               className="mx-2"/>
                        <label className="text-lg">
                            {intl.formatMessage({id: "i11114"})}
                        </label>
                    </div>
                </div>
                <div className="flex justify-between items-center">
                    <div>
                        <label className="text-lg"> {intl.formatMessage({id: "i11115"})} : {pagination?.totalCount}</label>
                        <label className="text-lg"> {intl.formatMessage({id: "i11116"})} : {pagination?.count}</label>
                    </div>
                    <CategorySearchBar className={"w-1/3 float-right"}
                                       categories={searchCategory}
                                       defaultCategory={defaultCategory}
                                       defaultKeyword={defaultKeyword}
                                       onSubmit={onSubmitKeyword}/>
                </div>
                <div className="h-[80%]">
                    {
                        pagination && pagination.count > 0 ?
                            <WorkspaceTable
                                pagination={pagination}
                            />
                            :
                            <div className={"w-full h-full flex justify-center items-center"}>
                                {intl.formatMessage({id: "i10027"})}
                            </div>
                    }
                </div>
                <div className="flex justify-end">
                    <PageBar page={defaultPage} lastPage={lastPage} onClickPage={onClickPage}/>
                </div>
            </div>
        </div>
    );
};


type WorkspaceTableProps = {
    pagination: workspacePage,
}

const WorkspaceTable: React.FC<WorkspaceTableProps> = (props) => {
    const {pagination} = props;
    const intl = useIntl();
    const navigate = useNavigate();

    const redirect = (workspace: Workspace) => {
        navigate(workspace._id, {state: {workspace: workspace}});
    }
    return (
        <div className="py-2 min-w-full">
            <div className={"flex flex-col justify-between"}>
                <div className="">
                    <table className="w-full p-5 table-fixed break-keep sm:rounded-lg border-2">
                        <thead className="w-full text-sm font-bold text-left border-b bg-gray-200">
                        <tr className="text-[16px]">
                            <th scope="col" className="px-5 py-4 text-left w-full">
                                {intl.formatMessage({id: "i1001"})}
                            </th>
                            <th scope="col" className="p-2.5 py-4 text-left w-[10%]">
                                {intl.formatMessage({id: "i11105"})}
                            </th>
                            <th scope="col" className="p-2.5 py-4 text-left w-[17%]">
                                {intl.formatMessage({id: "i11106"})}
                            </th>
                            <th scope="col" className="p-2.5 py-4 text-left w-[25%]">
                                {intl.formatMessage({id: "i1003"})}
                            </th>
                            <th scope="col" className="p-2.5 py-4 text-left w-[24%]">
                                {intl.formatMessage({id: "i11104"})}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {pagination.workspaces.map((workspace, index) => {
                                return (
                                    <WorkspaceRow
                                        key={workspace._id}
                                        workspace={workspace}
                                        redirect={() => {
                                            redirect(workspace)
                                        }}
                                    />)
                            }
                        )
                        }

                        {(pagination.workspaces.length < PaginationSize) && (
                            [...Array(PaginationSize - pagination?.workspaces.length)].map((v) => <tr
                                className={"h-[67px]"}></tr>)
                        )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default WorkspaceView;
